"use client";

import { cn } from "@shared/src/utils/cn";
import { IoChevronForward } from "react-icons/io5";
import { Button } from "@shared/src/components/ui/button";
import { useRouter } from "next/navigation";
import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { useP2POrders } from "@shared/src/components/modules/orders/use-p2p-orders";
import { TextWithCopy } from "@shared/src/components/common/misc/TextWithCopy";
import { getTargetDate } from "@shared/src/utils/order";
import { Skeleton } from "@shared/src/components/ui/skeleton";
import { Chip } from "@shared/src/components/ui/chip";
import { OrderResponse } from "@sdk/shared/types/order.types";
import { PendingOrderCard } from "@shared/src/components/modules/orders/PendingOrderCard";
import { useEffect } from "react";

interface PendingOrderItem {
  id: string;
  buyerName: string;
  orderNumber: string;
  type: "Buy" | "Sell";
  amount: {
    value: number;
    currency: string;
  };
  payment: {
    value: string;
    method: string;
  };
  timeLeft: string;
}

function PendingOrderSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map(item => (
        <div key={item} className="flex flex-col p-4 rounded-2xl border-2 border-surfaces-bg-secondary">
          <div className="flex items-center justify-between mb-2">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-16" />
            </div>
            <div className="text-right space-y-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4">
            <div className="flex w-full gap-2 md:w-auto">
              <Skeleton className="h-10 w-24 rounded-2xl" />
              <Skeleton className="h-10 w-32 rounded-full" />
            </div>
            <div className="flex w-full gap-2 md:w-auto">
              <Skeleton className="h-10 w-24 rounded-full" />
              <Skeleton className="h-10 w-24 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface PendingOrderProps {
  className?: string;
  orders?: PendingOrderItem[];
  context?: "dropdown" | "sheet" | "nested-dropdown";
  onNavigate?: () => void;
}

export function PendingOrder({ className, context = "dropdown", onNavigate }: PendingOrderProps) {
  const router = useRouter();
  const { data: ordersRes, isLoading } = useP2POrders({ limit: 3, page: 1, state: "in_progress" });
  const orders = ordersRes?.orders;
  console.log("pendning orders", orders);

  const handleNavigation = () => {
    if (onNavigate) {
      onNavigate();
    }
    router.push("/p2p-orders");
  };

  const ViewAllButton = () => {
    if (context === "sheet") {
      return (
        <button
          onClick={handleNavigation}
          className="flex items-center cursor-pointer gap-2 text-small hover:underline underline-offset-8"
        >
          View all
          <Chip>{ordersRes?.totalItems || ordersRes?.orders?.length || 0} </Chip>
          <IoChevronForward size={16} />
        </button>
      );
    }

    return (
      <DropdownMenuItem
        onSelect={handleNavigation}
        className="flex items-center cursor-pointer gap-2 text-small hover:underline underline-offset-8"
      >
        View all
        <Chip>{ordersRes?.totalItems || ordersRes?.orders?.length || 0} </Chip>
        <IoChevronForward size={16} />
      </DropdownMenuItem>
    );
  };

  // if (!orders) {
  //   return null;
  // }

  return (
    <div className={cn("mt-4 w-[600px]", className)}>
      <div className="flex items-center justify-between mb-4">
        <h6>Pending Orders</h6>
        <ViewAllButton />
      </div>
      <hr className="border-t border-surfaces-bg-tertiary my-8" />
      {isLoading && <PendingOrderSkeleton />}
      {orders?.length === 0 ? (
        <div className="flex text-h6 items-center justify-center p-6 rounded-2xl w-full bg-transparent text-text-secondary">
          No pending orders yet!
        </div>
      ) : (
        <div className="space-y-4">
          {orders?.map(order => (
            <PendingOrderCard key={order.id} order={order} context={context} />
          ))}
        </div>
      )}
    </div>
  );
}
